import isDeepEqual from '@/lib/isDeepEqual.js';
import { shouldHideToolCallInActivityFeed } from '@shared/utils/pieveActivityFeedVisibility.js';
import getPieveToolCallDisplayText, { getPieveToolCallFileInfo } from '@/lib/platform/getPieveToolCallDisplayText.js';

const FILE_LINK_TOOL_NAMES = new Set(['files_read', 'files_write', 'files_stat', 'files_move', 'files_delete', 'workspace_read', 'workspace_write', 'workspace_stat', 'workspace_move', 'workspace_delete', 'desktop_read_file', 'desktop_write_file', 'desktop_stat', 'desktop_move', 'desktop_delete', 'desktop_apply_patch', 'patch_apply']);
const BINARY_FILE_EXTENSIONS = new Set(['7z', 'a', 'avi', 'avif', 'bin', 'bmp', 'class', 'cur', 'dll', 'dmg', 'doc', 'docx', 'dylib', 'eot', 'exe', 'gif', 'gz', 'heic', 'heif', 'ico', 'iso', 'jar', 'jpeg', 'jpg', 'lockb', 'm4a', 'm4v', 'moov', 'mov', 'mp3', 'mp4', 'mpeg', 'mpg', 'o', 'obj', 'otf', 'pdf', 'pkg', 'png', 'ppt', 'pptx', 'pyc', 'rar', 'so', 'tar', 'tif', 'tiff', 'ttf', 'wav', 'webm', 'webp', 'woff', 'woff2', 'xls', 'xlsx', 'zip']);

const toSegments = (text, tone = 'muted') => {
  const normalized = String(text || '').trim();

  if (!normalized) {
    return [];
  }

  return [{ text: normalized, tone }];
};

const isLikelyTextFilePath = (value) => {
  const normalized = String(value || '')
    .trim()
    .replace(/\\/g, '/');

  if (!normalized) {
    return false;
  }

  const leaf = normalized.split('/').filter(Boolean).at(-1) || '';
  const extension = leaf.includes('.') ? String(leaf.split('.').at(-1) || '').toLowerCase() : '';

  if (!extension) {
    return true;
  }

  return !BINARY_FILE_EXTENSIONS.has(extension);
};

const toFormattedDiffCount = (value) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return Math.floor(parsed).toLocaleString();
};

const toDiffCountIntegerOrNull = (value) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return Math.floor(parsed);
};

const isZeroDiffCountPair = ({ additions, deletions } = {}) => {
  const normalizedAdditions = toDiffCountIntegerOrNull(additions);
  const normalizedDeletions = toDiffCountIntegerOrNull(deletions);

  if (normalizedAdditions === null || normalizedDeletions === null) {
    return false;
  }

  return normalizedAdditions === 0 && normalizedDeletions === 0;
};

const appendDiffCountSegments = ({ segments, additions, deletions }) => {
  const safeSegments = Array.isArray(segments) ? [...segments] : [];
  const formattedAdditions = toFormattedDiffCount(additions);
  const formattedDeletions = toFormattedDiffCount(deletions);

  if (isZeroDiffCountPair({ additions, deletions })) {
    return safeSegments;
  }

  if (formattedAdditions === null && formattedDeletions === null) {
    return safeSegments;
  }

  if (formattedAdditions !== null) {
    safeSegments.push({ text: ` +${formattedAdditions}`, tone: 'success' });
  }

  if (formattedDeletions !== null) {
    safeSegments.push({ text: ` -${formattedDeletions}`, tone: 'danger' });
  }

  return safeSegments;
};

const toToolCallSegments = ({ toolName, args, tone = 'muted', displayText } = {}) => {
  if (shouldHideToolCallInActivityFeed({ toolName, args })) {
    return [];
  }

  const normalizedText = String(displayText || getPieveToolCallDisplayText({ toolName, args }) || '').trim();

  if (!normalizedText) {
    return [];
  }

  const { fileName, filePath, additions, deletions } = getPieveToolCallFileInfo({ toolName, args });
  const normalizedToolName = String(toolName || '')
    .trim()
    .toLowerCase();
  const shouldUseAccentFileLink = FILE_LINK_TOOL_NAMES.has(normalizedToolName) && isLikelyTextFilePath(filePath);

  if (!fileName || !filePath || !shouldUseAccentFileLink) {
    return toSegments(normalizedText, tone);
  }

  const fileIndex = normalizedText.lastIndexOf(fileName);

  if (fileIndex < 0) {
    const suffix = normalizedText && !/\s$/.test(normalizedText) ? ' ' : '';
    return appendDiffCountSegments({
      additions,
      deletions,
      segments: [
        { text: `${normalizedText}${suffix}`, tone },
        { text: fileName, tone: 'link', filePath }
      ]
    });
  }

  const beforeText = normalizedText.slice(0, fileIndex);
  const afterText = normalizedText.slice(fileIndex + fileName.length);
  const segments = [];

  if (beforeText) {
    const paddedBeforeText = !/\s$/.test(beforeText) ? `${beforeText} ` : beforeText;
    segments.push({ text: paddedBeforeText, tone });
  }

  segments.push({ text: fileName, tone: 'link', filePath });

  if (afterText) {
    segments.push({ text: afterText, tone });
  }

  return appendDiffCountSegments({
    additions,
    deletions,
    segments
  });
};

const parseToolResultOutputFromEvent = (event = {}) => {
  const objectOutput = event?.output && typeof event.output === 'object' && !Array.isArray(event.output) ? event.output : event?.toolResultOutput && typeof event.toolResultOutput === 'object' && !Array.isArray(event.toolResultOutput) ? event.toolResultOutput : null;

  if (objectOutput) {
    return objectOutput;
  }

  const code = String(event?.code || '').trim();

  if (!code) {
    return null;
  }

  try {
    const parsed = JSON.parse(code);

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    // Tool output is not guaranteed to be JSON.
  }

  return null;
};

const mergeToolCallArgsWithResultOutput = ({ args, output } = {}) => {
  const safeArgs = args && typeof args === 'object' && !Array.isArray(args) ? args : {};
  const safeOutput = output && typeof output === 'object' && !Array.isArray(output) ? output : null;

  if (!safeOutput) {
    return safeArgs;
  }

  return {
    ...safeArgs,
    response: safeOutput,
    output: safeOutput,
    result: safeOutput
  };
};

const enrichToolCallEventWithResultOutput = ({ toolCallEvent, toolResultOutput } = {}) => {
  if (!toolCallEvent || toolCallEvent?.type !== 'line') {
    return null;
  }

  const toolName = String(toolCallEvent?.toolName || '').trim();

  if (!toolName) {
    return null;
  }

  // completed rides in the args so display-label generation flips to past
  // tense even when the result output itself spilled to an artifact and no
  // inline response object exists to merge.
  const nextArgs = {
    ...mergeToolCallArgsWithResultOutput({
      args: toolCallEvent?.toolArgs,
      output: toolResultOutput
    }),
    completed: true
  };
  const nextSegments = toToolCallSegments({
    toolName,
    args: nextArgs,
    tone: 'muted'
  });
  /* A hidden call stays hidden once its result lands. A plan run's hidden step (Claude Code's to-do list, the plan
     run's wait for answers) keeps Claude Code's tool name, which only the plan-run mapping knows to hide; before
     this, each one came back as "Ran todowrite" once its result arrived (2026-10-30). */
  const nextHiddenInActivityFeed =
    toolCallEvent?.hiddenInActivityFeed === true ||
    shouldHideToolCallInActivityFeed({
      toolName,
      args: nextArgs
    });

  if (isDeepEqual(nextSegments, toolCallEvent?.segments) && isDeepEqual(nextArgs, toolCallEvent?.toolArgs) && Boolean(toolCallEvent?.hiddenInActivityFeed) === nextHiddenInActivityFeed) {
    return null;
  }

  return {
    ...toolCallEvent,
    toolArgs: nextArgs,
    segments: nextSegments,
    completed: true,
    hiddenInActivityFeed: nextHiddenInActivityFeed
  };
};

const reconcileToolCallEventsWithToolResults = (events = []) => {
  const safeEvents = Array.isArray(events) ? events : [];

  if (safeEvents.length < 2) {
    return safeEvents;
  }

  const toolResultOutputByCallId = new Map();

  for (const event of safeEvents) {
    if (event?.type !== 'codeBlock' && event?.type !== 'artifactPreview') {
      continue;
    }

    const callId = String(event?.callId || '').trim();

    if (!callId) {
      continue;
    }

    // A result with no parseable inline output (e.g. spilled to an artifact)
    // still marks its call completed; parsed output additionally enriches args.
    const output = parseToolResultOutputFromEvent(event);

    if (output || !toolResultOutputByCallId.has(callId)) {
      toolResultOutputByCallId.set(callId, output || null);
    }
  }

  if (toolResultOutputByCallId.size < 1) {
    return safeEvents;
  }

  let didChange = false;
  const nextEvents = safeEvents.map((event) => {
    if (event?.type !== 'line') {
      return event;
    }

    const callId = String(event?.callId || '').trim();

    if (!callId) {
      return event;
    }

    if (!toolResultOutputByCallId.has(callId)) {
      return event;
    }

    const toolResultOutput = toolResultOutputByCallId.get(callId);
    const enriched = enrichToolCallEventWithResultOutput({
      toolCallEvent: event,
      toolResultOutput
    });

    if (!enriched) {
      return event;
    }

    didChange = true;
    return enriched;
  });

  if (!didChange) {
    return safeEvents;
  }

  return nextEvents;
};

export { enrichToolCallEventWithResultOutput, mergeToolCallArgsWithResultOutput, parseToolResultOutputFromEvent, reconcileToolCallEventsWithToolResults, toToolCallSegments };
